import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import LinearProgress from "@mui/material/LinearProgress";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import RefreshIcon from "@mui/icons-material/Refresh";
import VerifiedIcon from "@mui/icons-material/Verified";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import moment from "moment";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

import { GetAdminStudyPlanCall } from "../../redux/api/studyPlan.api";

const GOAL_LABEL = {
  exam: "Exam",
  mastery: "Mastery",
  general: "General",
};

function formatDateTime(value) {
  if (!value) return "—";
  return moment(value).format("YYYY-MM-DD HH:mm");
}

function formatBytes(bytes) {
  const n = Number(bytes) || 0;
  if (!n) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function initials(name, email) {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  const combo = ((parts[0]?.[0] || "") + (parts[1]?.[0] || "")).toUpperCase();
  if (combo) return combo;
  if (email) return email.charAt(0).toUpperCase();
  return "?";
}

function EmptyState({ message }) {
  return (
    <Box sx={{ py: 6, textAlign: "center" }}>
      <Typography variant="body2" color="text.secondary">
        {message}
      </Typography>
    </Box>
  );
}

function MetaCell({ label, value }) {
  return (
    <Grid item xs={6} md={3}>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ textTransform: "uppercase", letterSpacing: "0.06em" }}
      >
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={600} sx={{ wordBreak: "break-word" }}>
        {value || "—"}
      </Typography>
    </Grid>
  );
}

function isPdf(material) {
  const mime = String(material?.mimeType || "").toLowerCase();
  const name = String(material?.fileName || "").toLowerCase();
  return mime.includes("pdf") || name.endsWith(".pdf");
}

function isImage(material) {
  const mime = String(material?.mimeType || "").toLowerCase();
  const name = String(material?.fileName || "").toLowerCase();
  return mime.startsWith("image/") || /\.(png|jpe?g|gif|webp)$/.test(name);
}

function isAudio(material) {
  const mime = String(material?.mimeType || "").toLowerCase();
  const name = String(material?.fileName || "").toLowerCase();
  return mime.startsWith("audio/") || /\.(mp3|m4a|wav)$/.test(name);
}

function MaterialsTab({ materials }) {
  const uploads = (materials || []).filter((item) => item.origin === "user_upload");
  const library = (materials || []).filter((item) => item.origin !== "user_upload");
  const [activeId, setActiveId] = useState(uploads[0]?.id || materials?.[0]?.id || "");

  useEffect(() => {
    if (!activeId && (uploads[0]?.id || materials?.[0]?.id)) {
      setActiveId(uploads[0]?.id || materials[0].id);
    }
  }, [activeId, uploads, materials]);

  if (!materials?.length) {
    return <EmptyState message="No materials attached to this study plan." />;
  }

  const active = materials.find((item) => item.id === activeId) || materials[0];
  const figures = Array.isArray(active?.figures) ? active.figures.filter((f) => f?.url) : [];

  return (
    <Stack spacing={2.5}>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        {materials.map((material) => (
          <Chip
            key={material.id}
            clickable
            color={material.id === active?.id ? "primary" : "default"}
            variant={material.id === active?.id ? "filled" : "outlined"}
            label={
              material.origin === "user_upload"
                ? material.fileName || "Upload"
                : material.sourceLabel || material.origin || "Library"
            }
            onClick={() => setActiveId(material.id)}
            sx={{ fontWeight: 600, maxWidth: 280 }}
          />
        ))}
      </Stack>

      {uploads.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          This plan has no user uploads — only Microlab library sources.
        </Typography>
      ) : null}

      {library.length && active?.origin !== "user_upload" ? (
        <Paper
          variant="outlined"
          sx={{ p: 2, borderRadius: 2.5, bgcolor: "background.default" }}
        >
          <Typography variant="subtitle2" fontWeight={700}>
            {active.sourceLabel || "Microlab library"}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
            {active.provenanceNote || "Approved Microlab catalog source."}
          </Typography>
        </Paper>
      ) : null}

      {active?.origin === "user_upload" ? (
        <Paper variant="outlined" sx={{ p: { xs: 1.5, md: 2 }, borderRadius: 2.5 }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.5}
            alignItems={{ xs: "flex-start", sm: "center" }}
            justifyContent="space-between"
            mb={2}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="subtitle1" fontWeight={700} noWrap>
                {active.fileName || "Uploaded file"}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {[active.mimeType, formatBytes(active.fileSizeBytes)].filter(Boolean).join(" · ")}
              </Typography>
            </Box>
            {active.fileUrl ? (
              <Button
                size="small"
                variant="outlined"
                startIcon={<OpenInNewIcon />}
                href={active.fileUrl}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ textTransform: "none", fontWeight: 700 }}
              >
                Open file
              </Button>
            ) : null}
          </Stack>

          {!active.fileUrl ? (
            <Typography variant="body2" color="text.secondary">
              File URL is not available for this upload.
            </Typography>
          ) : isPdf(active) ? (
            <Box
              component="iframe"
              title={active.fileName || "PDF"}
              src={active.fileUrl}
              sx={{
                width: "100%",
                height: { xs: 420, md: 640 },
                border: 0,
                borderRadius: 2,
                bgcolor: "grey.100",
              }}
            />
          ) : isImage(active) ? (
            <Box
              component="img"
              src={active.fileUrl}
              alt={active.fileName || "Upload"}
              sx={{
                display: "block",
                maxWidth: "100%",
                maxHeight: 640,
                mx: "auto",
                borderRadius: 2,
              }}
            />
          ) : isAudio(active) ? (
            <Box component="audio" controls src={active.fileUrl} sx={{ width: "100%" }} />
          ) : (
            <Typography variant="body2" color="text.secondary">
              Preview is not available for this file type. Use Open file to view it.
            </Typography>
          )}

          {figures.length ? (
            <Box mt={2.5}>
              <Typography variant="subtitle2" fontWeight={700} mb={1}>
                Extracted figures ({figures.length})
              </Typography>
              <Grid container spacing={1.5}>
                {figures.map((figure) => (
                  <Grid item xs={6} sm={4} md={3} key={figure.id}>
                    <Box
                      component="a"
                      href={figure.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ display: "block" }}
                    >
                      <Box
                        component="img"
                        src={figure.url}
                        alt={figure.text || "Figure"}
                        sx={{
                          width: "100%",
                          height: 120,
                          objectFit: "cover",
                          borderRadius: 1.5,
                          border: "1px solid",
                          borderColor: "divider",
                        }}
                      />
                    </Box>
                    {figure.page ? (
                      <Typography variant="caption" color="text.secondary">
                        Page {figure.page}
                      </Typography>
                    ) : null}
                  </Grid>
                ))}
              </Grid>
            </Box>
          ) : null}

          {active.extractedText ? (
            <Box mt={2.5}>
              <Typography variant="subtitle2" fontWeight={700} mb={1}>
                Extracted text
              </Typography>
              <Paper
                variant="outlined"
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  maxHeight: 280,
                  overflow: "auto",
                  bgcolor: "background.default",
                  whiteSpace: "pre-wrap",
                  fontSize: 13,
                  lineHeight: 1.55,
                }}
              >
                {active.extractedText}
              </Paper>
            </Box>
          ) : null}
        </Paper>
      ) : null}
    </Stack>
  );
}

function TopicsTab({ topics, lessons, selectedTopicId, onSelectTopic }) {
  if (!topics?.length) return <EmptyState message="No topics on this plan." />;

  return (
    <Stack spacing={1.25}>
      {topics.map((topic, index) => {
        const lesson = lessons.find((item) => item.topicId === topic.id);
        const summary = topic.lesson;
        const selected = topic.id === selectedTopicId;
        const status = summary?.status || (lesson ? "ready" : "not started");
        return (
          <Paper
            key={topic.id}
            variant="outlined"
            onClick={() => onSelectTopic(topic.id)}
            sx={{
              p: 1.75,
              borderRadius: 2.5,
              cursor: "pointer",
              borderColor: selected ? "primary.main" : "divider",
              boxShadow: selected ? "0 0 0 2px rgba(37,99,235,0.18)" : "none",
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Chip size="small" label={index + 1} sx={{ fontWeight: 700 }} />
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="subtitle2" fontWeight={700} noWrap>
                  {topic.title}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {topic.origin || "topic"}
                  {summary?.masteryPercent != null ? ` · mastery ${summary.masteryPercent}%` : ""}
                  {summary?.completionPercent != null
                    ? ` · progress ${summary.completionPercent}%`
                    : ""}
                </Typography>
              </Box>
              <Chip
                size="small"
                color={
                  status === "completed"
                    ? "success"
                    : status === "ready" || status === "in_progress"
                      ? "info"
                      : "default"
                }
                variant="outlined"
                label={status}
                sx={{ fontWeight: 600, textTransform: "capitalize" }}
              />
            </Stack>
          </Paper>
        );
      })}
    </Stack>
  );
}

function LessonTab({ lesson, topicTitle }) {
  if (!lesson) {
    return (
      <EmptyState message="No generated lesson for this topic yet. The student has not opened it." />
    );
  }

  const steps = Array.isArray(lesson.steps) ? lesson.steps : [];
  const quiz = Array.isArray(lesson.finalQuiz) ? lesson.finalQuiz : [];
  const cards = Array.isArray(lesson.flashcards) ? lesson.flashcards : [];
  const progress = lesson.progress || {};

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="h6" fontWeight={800}>
          {lesson.title || topicTitle || "Lesson"}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Read-only view of what the student sees
          {progress.status ? ` · ${progress.status}` : ""}
          {progress.masteryPercent != null ? ` · mastery ${progress.masteryPercent}%` : ""}
        </Typography>
        {progress.completionPercent != null ? (
          <LinearProgress
            variant="determinate"
            value={Math.max(0, Math.min(100, Number(progress.completionPercent) || 0))}
            sx={{ mt: 1.5, height: 8, borderRadius: 999 }}
          />
        ) : null}
      </Box>

      {lesson.podcast?.url ? (
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2.5 }}>
          <Typography variant="subtitle2" fontWeight={700} mb={1}>
            Podcast
          </Typography>
          <Box component="audio" controls src={lesson.podcast.url} sx={{ width: "100%" }} />
        </Paper>
      ) : null}

      <Box>
        <MDTypography variant="button" color="info" fontWeight="bold" sx={{ mb: 1.5, display: "block" }}>
          Reading steps ({steps.length})
        </MDTypography>
        {!steps.length ? (
          <EmptyState message="No reading steps in this lesson." />
        ) : (
          <Stack spacing={1.5}>
            {steps.map((step, index) => (
              <Paper key={step.id || index} variant="outlined" sx={{ p: 2, borderRadius: 2.5 }}>
                <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                  <Chip size="small" label={step.kind || "step"} sx={{ fontWeight: 700 }} />
                  <Typography variant="subtitle2" fontWeight={700}>
                    {step.title || `Step ${index + 1}`}
                  </Typography>
                </Stack>
                {step.body || step.text ? (
                  <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", lineHeight: 1.6 }}>
                    {step.body || step.text}
                  </Typography>
                ) : null}
                {step.kind === "check" && step.question ? (
                  <Box mt={1.5}>
                    <Typography variant="body2" fontWeight={700} mb={1}>
                      {step.question.prompt || step.question.question || "Check"}
                    </Typography>
                    <Stack spacing={0.75}>
                      {(step.question.choices || step.question.options || []).map((choice, i) => {
                        const correct = (step.question.correctIndexes || []).map(Number).includes(i);
                        return (
                          <Stack
                            key={i}
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            sx={{
                              p: 1,
                              borderRadius: 1.5,
                              bgcolor: correct ? "rgba(22,163,74,0.1)" : "background.default",
                              border: "1px solid",
                              borderColor: correct ? "success.main" : "divider",
                            }}
                          >
                            {correct ? (
                              <CheckCircleIcon color="success" fontSize="small" />
                            ) : (
                              <HighlightOffIcon color="disabled" fontSize="small" />
                            )}
                            <Typography variant="body2">{choice}</Typography>
                          </Stack>
                        );
                      })}
                    </Stack>
                  </Box>
                ) : null}
              </Paper>
            ))}
          </Stack>
        )}
      </Box>

      <Box>
        <MDTypography variant="button" color="info" fontWeight="bold" sx={{ mb: 1.5, display: "block" }}>
          Final quiz ({quiz.length})
        </MDTypography>
        {!quiz.length ? (
          <EmptyState message="No quiz questions in this lesson." />
        ) : (
          <Stack spacing={1.5}>
            {quiz.map((question, index) => (
              <Paper key={question.id || index} variant="outlined" sx={{ p: 2, borderRadius: 2.5 }}>
                <Typography variant="subtitle2" fontWeight={700} mb={1}>
                  {index + 1}. {question.prompt || question.question || "Question"}
                </Typography>
                <Stack spacing={0.75}>
                  {(question.choices || question.options || []).map((choice, i) => {
                    const correct = (question.correctIndexes || []).map(Number).includes(i);
                    return (
                      <Stack
                        key={i}
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        sx={{
                          p: 1,
                          borderRadius: 1.5,
                          bgcolor: correct ? "rgba(22,163,74,0.1)" : "background.default",
                          border: "1px solid",
                          borderColor: correct ? "success.main" : "divider",
                        }}
                      >
                        {correct ? (
                          <CheckCircleIcon color="success" fontSize="small" />
                        ) : (
                          <HighlightOffIcon color="disabled" fontSize="small" />
                        )}
                        <Typography variant="body2">{choice}</Typography>
                      </Stack>
                    );
                  })}
                </Stack>
              </Paper>
            ))}
          </Stack>
        )}
      </Box>

      <Box>
        <MDTypography variant="button" color="info" fontWeight="bold" sx={{ mb: 1.5, display: "block" }}>
          Flashcards ({cards.length})
        </MDTypography>
        {!cards.length ? (
          <EmptyState message="No flashcards in this lesson." />
        ) : (
          <Grid container spacing={1.5}>
            {cards.map((card, index) => (
              <Grid item xs={12} md={6} key={card.id || index}>
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2.5, height: "100%" }}>
                  <Typography variant="caption" color="text.secondary">
                    Front
                  </Typography>
                  <Typography variant="body2" fontWeight={700} sx={{ mb: 1.25 }}>
                    {card.front || card.question || "—"}
                  </Typography>
                  <Divider sx={{ mb: 1.25 }} />
                  <Typography variant="caption" color="text.secondary">
                    Back
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                    {card.back || card.answer || "—"}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>

      {Array.isArray(lesson.sources) && lesson.sources.length ? (
        <Box>
          <Typography variant="subtitle2" fontWeight={700} mb={1}>
            Sources
          </Typography>
          <Stack spacing={0.75}>
            {lesson.sources.map((source) => (
              <Typography key={source.id || source.label} variant="body2" color="text.secondary">
                · {source.label}
              </Typography>
            ))}
          </Stack>
        </Box>
      ) : null}
    </Stack>
  );
}

function OverviewTab({ plan }) {
  return (
    <Stack spacing={2}>
      <Grid container spacing={2}>
        <MetaCell label="Goal" value={GOAL_LABEL[plan.goal] || plan.goal} />
        <MetaCell label="Level" value={plan.level} />
        <MetaCell label="Target mastery" value={plan.targetMastery} />
        <MetaCell label="Language" value={plan.materialLanguage} />
        <MetaCell label="Exam" value={plan.examName} />
        <MetaCell label="Exam date" value={plan.examDate} />
        <MetaCell label="Status" value={plan.status} />
        <MetaCell
          label="Formats"
          value={(plan.preferences || []).join(", ") || "—"}
        />
      </Grid>
      {plan.accuracyNote ? (
        <Typography variant="body2" color="text.secondary">
          {plan.accuracyNote}
        </Typography>
      ) : null}
    </Stack>
  );
}

export default function StudyPlanUsageDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [plan, setPlan] = useState(null);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("materials");
  const [selectedTopicId, setSelectedTopicId] = useState("");

  const load = useCallback(() => {
    if (!id) return;
    setLoading(true);
    setError("");
    GetAdminStudyPlanCall(id).then((res) => {
      if (res?.status === 200 && res.plan) {
        setPlan(res.plan);
        const firstTopic =
          res.plan.topics?.find((topic) =>
            (res.plan.lessons || []).some((lesson) => lesson.topicId === topic.id)
          )?.id || res.plan.topics?.[0]?.id || "";
        setSelectedTopicId((current) => current || firstTopic);
      } else {
        setPlan(null);
        setError(res?.message || "Study plan could not be loaded.");
      }
      setLoading(false);
    });
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const selectedLesson = useMemo(() => {
    if (!plan || !selectedTopicId) return null;
    return (plan.lessons || []).find((lesson) => lesson.topicId === selectedTopicId) || null;
  }, [plan, selectedTopicId]);

  const selectedTopic = useMemo(
    () => (plan?.topics || []).find((topic) => topic.id === selectedTopicId) || null,
    [plan, selectedTopicId]
  );

  const progress = Math.max(0, Math.min(100, Number(plan?.progress) || 0));
  const uploadCount = (plan?.materials || []).filter((item) => item.origin === "user_upload").length;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox
        component="main"
        sx={{
          py: { xs: 3, md: 4 },
          px: { xs: 2, sm: 3, md: 4 },
          maxWidth: 1400,
          mx: "auto",
        }}
      >
        <Stack spacing={2.5}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate("/copilot-usage?section=study")}
              size="small"
              sx={{ color: "text.primary", textTransform: "none" }}
            >
              Back to Study plans
            </Button>
            <Box sx={{ flex: 1 }} />
            <Tooltip title="Refresh">
              <IconButton
                onClick={load}
                size="small"
                sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2 }}
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>

          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, md: 3 },
              borderRadius: 3,
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            {loading ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress size={32} />
              </Box>
            ) : !plan ? (
              <Typography color="text.secondary">
                {error || "This study plan could not be found."}
              </Typography>
            ) : (
              <Stack spacing={2}>
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  spacing={2}
                  alignItems={{ xs: "flex-start", md: "center" }}
                  justifyContent="space-between"
                >
                  <Stack direction="row" spacing={2} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
                    <Avatar
                      src={plan.user?.profileImage || undefined}
                      sx={{ width: 48, height: 48, bgcolor: "primary.main", fontSize: 16 }}
                    >
                      {initials(plan.user?.name, plan.user?.email)}
                    </Avatar>
                    <Box sx={{ minWidth: 0 }}>
                      <Stack direction="row" spacing={0.75} alignItems="center">
                        <MDTypography
                          variant="h5"
                          fontWeight="bold"
                          sx={{ wordBreak: "break-word", lineHeight: 1.3 }}
                        >
                          {plan.subject || "Study plan"}
                        </MDTypography>
                        {plan.user?.profileVerified ? (
                          <VerifiedIcon sx={{ fontSize: 18, color: "info.main" }} />
                        ) : null}
                      </Stack>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                        {[plan.user?.name, plan.user?.email, plan.user?.university]
                          .filter(Boolean)
                          .join(" · ") || "Unknown user"}
                      </Typography>
                    </Box>
                  </Stack>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Chip label={`${progress}%`} color="primary" size="small" sx={{ fontWeight: 700 }} />
                    <Chip
                      label={`${plan.topicsDone || 0}/${plan.topicsTotal || 0} topics`}
                      variant="outlined"
                      size="small"
                      sx={{ fontWeight: 600 }}
                    />
                    <Chip
                      label={`${uploadCount} upload${uploadCount === 1 ? "" : "s"}`}
                      variant="outlined"
                      size="small"
                      sx={{ fontWeight: 600 }}
                    />
                    {plan.learnWithAi ? (
                      <Chip label="AI library" variant="outlined" size="small" sx={{ fontWeight: 600 }} />
                    ) : null}
                  </Stack>
                </Stack>

                <Divider />

                <Grid container spacing={2}>
                  <MetaCell label="Created" value={formatDateTime(plan.createdAt)} />
                  <MetaCell label="Updated" value={formatDateTime(plan.updatedAt)} />
                  <MetaCell label="Goal" value={GOAL_LABEL[plan.goal] || plan.goal} />
                  <MetaCell label="Level" value={plan.level} />
                </Grid>
              </Stack>
            )}
          </Paper>

          {plan ? (
            <Paper
              elevation={0}
              sx={{
                borderRadius: 3,
                border: "1px solid",
                borderColor: "divider",
                overflow: "hidden",
              }}
            >
              <Tabs
                value={tab}
                onChange={(_e, next) => setTab(next)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  px: 1,
                  borderBottom: "1px solid",
                  borderColor: "divider",
                  "& .MuiTab-root": { textTransform: "none", fontWeight: 700, minHeight: 48 },
                }}
              >
                <Tab value="materials" label="Materials & file" />
                <Tab value="topics" label={`Topics (${plan.topics?.length || 0})`} />
                <Tab value="lesson" label="Lesson (read-only)" />
                <Tab value="overview" label="Plan details" />
              </Tabs>
              <Box sx={{ p: { xs: 2, md: 3 } }}>
                {tab === "materials" ? <MaterialsTab materials={plan.materials} /> : null}
                {tab === "topics" ? (
                  <TopicsTab
                    topics={plan.topics}
                    lessons={plan.lessons}
                    selectedTopicId={selectedTopicId}
                    onSelectTopic={(topicId) => {
                      setSelectedTopicId(topicId);
                      setTab("lesson");
                    }}
                  />
                ) : null}
                {tab === "lesson" ? (
                  <Stack spacing={2}>
                    {(plan.topics || []).length > 1 ? (
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        {plan.topics.map((topic) => (
                          <Chip
                            key={topic.id}
                            clickable
                            size="small"
                            color={topic.id === selectedTopicId ? "primary" : "default"}
                            variant={topic.id === selectedTopicId ? "filled" : "outlined"}
                            label={topic.title}
                            onClick={() => setSelectedTopicId(topic.id)}
                            sx={{ fontWeight: 600, maxWidth: 220 }}
                          />
                        ))}
                      </Stack>
                    ) : null}
                    <LessonTab lesson={selectedLesson} topicTitle={selectedTopic?.title} />
                  </Stack>
                ) : null}
                {tab === "overview" ? <OverviewTab plan={plan} /> : null}
              </Box>
            </Paper>
          ) : null}
        </Stack>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}
